import { useQueryClient } from '@tanstack/react-query';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Switch } from '@whistler-ui/k2/components/Switch';
import { cn } from '@whistler-ui/k2/helpers';
import { useQuery } from '@whistler-ui/k2/hooks/useQuery';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useEntitlementsStore } from '../../store/entitlements';
import { IEnvironmentToggleProps } from './types';

const VIEW_PARAMS = {
  LIVE: 'live', // means production but not actual live production
  TEST: 'test',
} as const;

const VIEW_PARAM_KEY = 'view';

// CSS-only hover tooltip fed by data-tooltip (port of the old @include tooltip mixin).
const TOOLTIP_CLASSES = cn(
  'relative after:absolute after:right-0 after:z-[9999]',
  'after:hidden hover:after:block after:content-[attr(data-tooltip)]',
  'after:mt-[calc(calc(var(--size-4)*2)+var(--size-2))]',
  'after:whitespace-nowrap after:rounded-(--border-radius-base)',
  'after:bg-(--ui-bg-dark) after:p-(--size-2) after:text-white',
);

export const EnvironmentToggle: React.FC<IEnvironmentToggleProps> = props => {
  const componentName = 'c-EnvironmentToggle';
  const { disabled, name, tooltipText, isValidProductionUpgrade } = props;
  const { t } = useTranslation('components');
  const { getSearchParams, updateSearchParams } = useQuery();
  const queryClient = useQueryClient();

  const currentView = getSearchParams(VIEW_PARAM_KEY);

  const {
    isLiveView,
    current,
    toggleEnvironment: toggleEnvironmentAction,
  } = useEntitlementsStore();

  const isLiveViewActive = useMemo(() => {
    return !disabled && isLiveView;
  }, [isLiveView, disabled]);

  useEffect(() => {
    if (!currentView || disabled) {
      return;
    }

    if (currentView === VIEW_PARAMS.LIVE) {
      toggleEnvironmentAction(true);
    } else {
      toggleEnvironmentAction(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (disabled) {
      return;
    }

    updateSearchParams(
      VIEW_PARAM_KEY,
      isLiveView ? VIEW_PARAMS.LIVE : VIEW_PARAMS.TEST,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiveView]);

  const toggleEnvironment = useCallback(() => {
    if (disabled) {
      return;
    }
    queryClient.invalidateQueries({
      queryKey: ['get_selected_api_tests', current?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ['get_selected_use_case_tests', current?.id],
    });
    toggleEnvironmentAction(!isLiveView);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleEnvironmentAction, isLiveView, disabled]);

  const label = isLiveViewActive
    ? t('components:environment_toggle.live_view.label')
    : t('components:environment_toggle.test_view.label');

  return (
    <Stack
      direction="row"
      align="center"
      justify="center"
      className={cn(componentName, Boolean(tooltipText) && TOOLTIP_CLASSES)}
      data-tooltip={tooltipText}
    >
      <Switch
        variant="dual"
        id={name}
        name={name}
        checked={disabled ? false : isLiveViewActive}
        onChange={toggleEnvironment}
        disabled={disabled}
        // Green in live view, brand blue in test view — matching the label.
        activeColor="var(--success)"
        secondaryColor="var(--color-brand-primary)"
        aria-label={label}
        data-testid={`${componentName}__input$${name}`}
        data-prod-valid={isValidProductionUpgrade}
        data-is-live-view={isLiveViewActive}
      />

      <span
        className={cn('m-auto pl-(--size-2) pr-(--size-4) text-sm', {
          'text-(--success)': isLiveViewActive,
          'text-brand-primary': !isLiveViewActive,
        })}
        data-testid={`${componentName}$${name}`}
      >
        {label}
      </span>
    </Stack>
  );
};
